import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, Clock, Flame, ChefHat, Filter, X, Heart, 
  Leaf, Wheat, UtensilsCrossed, Apple, ChevronLeft, ChevronRight,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'all', label: 'Toate', icon: UtensilsCrossed, color: 'bg-slate-500' },
  { id: 'vegetarian', label: 'Vegetarian', icon: Leaf, color: 'bg-green-500' },
  { id: 'vegan', label: 'Vegan', icon: Apple, color: 'bg-emerald-500' },
  { id: 'traditional', label: 'Tradițional', icon: ChefHat, color: 'bg-amber-500' },
  { id: 'low_carb', label: 'Low Carb', icon: Wheat, color: 'bg-purple-500' },
  { id: 'general', label: 'General', icon: Heart, color: 'bg-rose-500' },
];

const MEAL_TYPES = [
  { id: 'all', label: 'Toate mesele' },
  { id: 'breakfast', label: 'Mic dejun' },
  { id: 'lunch', label: 'Prânz' },
  { id: 'dinner', label: 'Cină' },
  { id: 'snack', label: 'Gustări' },
];

const Recipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [mealType, setMealType] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchRecipes();
  }, [page, category, mealType]);

  useEffect(() => {
    fetchStats();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchRecipes();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      if (search) params.append('search', search);
      if (category !== 'all') params.append('category', category);
      if (mealType !== 'all') params.append('meal_type', mealType);
      
      const response = await axios.get(`${API}/recipes/public?${params}`);
      setRecipes(response.data.recipes || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/recipes/public/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getCategoryBadge = (cat) => {
    const found = CATEGORIES.find(c => c.id === cat);
    if (!found) return null;
    return (
      <Badge className={`${found.color} text-white`}>
        {found.label}
      </Badge>
    );
  };

  const getMealTypeBadge = (type) => {
    const labels = {
      breakfast: 'Mic dejun',
      lunch: 'Prânz', 
      dinner: 'Cină',
      snack: 'Gustare'
    };
    const colors = {
      breakfast: 'bg-yellow-100 text-yellow-800',
      lunch: 'bg-blue-100 text-blue-800',
      dinner: 'bg-indigo-100 text-indigo-800',
      snack: 'bg-pink-100 text-pink-800'
    };
    return (
      <Badge variant="outline" className={colors[type] || 'bg-gray-100'}>
        {labels[type] || type}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Heart className="h-8 w-8 text-green-600" />
            <span className="text-2xl font-bold text-green-800">NutriHealth</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/auth">
              <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                Autentificare
              </Button>
            </Link>
            <Link to="/auth">
              <Button className="bg-green-600 hover:bg-green-700">
                Începe Acum
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Explorează {stats?.total || '150+'} Rețete Sănătoase
          </h1>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Descoperă rețete delicioase pentru orice stil de viață - vegetarian, vegan, tradițional sau low-carb.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Caută rețete sau ingrediente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 pr-4 py-6 text-lg text-gray-900 bg-white rounded-full shadow-lg"
              data-testid="recipe-search-input"
            />
            {search && (
              <button 
                onClick={() => setSearch('')}
                className="absolute right-4 top-1/2 -translate-y-1/2"
              >
                <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Stats Pills */}
      {stats && (
        <section className="py-6 border-b bg-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-yellow-100 rounded-full">
                <span className="font-semibold text-yellow-800">{stats.by_meal_type?.breakfast || 0}</span>
                <span className="text-yellow-700">mic dejun</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full">
                <span className="font-semibold text-blue-800">{stats.by_meal_type?.lunch || 0}</span>
                <span className="text-blue-700">prânz</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full">
                <span className="font-semibold text-indigo-800">{stats.by_meal_type?.dinner || 0}</span>
                <span className="text-indigo-700">cină</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full">
                <span className="font-semibold text-pink-800">{stats.by_meal_type?.snack || 0}</span>
                <span className="text-pink-700">gustări</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="py-6 bg-gray-50 border-b">
        <div className="container mx-auto px-4">
          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isActive = category === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => { setCategory(cat.id); setPage(1); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                    isActive 
                      ? `${cat.color} text-white shadow-md` 
                      : 'bg-white text-gray-700 hover:bg-gray-100 border'
                  }`}
                  data-testid={`filter-category-${cat.id}`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{cat.label}</span>
                  {stats?.by_category?.[cat.id] && (
                    <span className={`text-xs ${isActive ? 'text-white/80' : 'text-gray-400'}`}>
                      ({stats.by_category[cat.id]})
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Meal Type Filters */}
          <div className="flex flex-wrap justify-center gap-2">
            {MEAL_TYPES.map((meal) => {
              const isActive = mealType === meal.id;
              return (
                <button
                  key={meal.id}
                  onClick={() => { setMealType(meal.id); setPage(1); }}
                  className={`px-4 py-2 rounded-lg transition-all ${
                    isActive 
                      ? 'bg-green-600 text-white' 
                      : 'bg-white text-gray-600 hover:bg-green-50 border'
                  }`}
                  data-testid={`filter-meal-${meal.id}`}
                >
                  {meal.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Results Count */}
      <section className="py-4">
        <div className="container mx-auto px-4">
          <p className="text-gray-600">
            {loading ? 'Se încarcă...' : `${total} rețete găsite`}
            {search && ` pentru "${search}"`}
          </p>
        </div>
      </section>

      {/* Recipe Grid */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <div className="h-48 bg-gray-200 rounded-t-lg" />
                  <CardContent className="p-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : recipes.length === 0 ? (
            <div className="text-center py-16">
              <ChefHat className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                Nu am găsit rețete
              </h3>
              <p className="text-gray-500">
                Încearcă să modifici filtrele sau termenul de căutare.
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => { setSearch(''); setCategory('all'); setMealType('all'); }}
              >
                Resetează filtrele
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {recipes.map((recipe) => (
                <Card 
                  key={recipe.id} 
                  className="group hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
                  data-testid={`recipe-card-${recipe.id}`}
                >
                  {/* Recipe Image Placeholder */}
                  <div className="h-48 bg-gradient-to-br from-green-100 to-emerald-200 relative overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ChefHat className="h-16 w-16 text-green-300 group-hover:scale-110 transition-transform" />
                    </div>
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex flex-col gap-1">
                      {getCategoryBadge(recipe.category)}
                    </div>
                    <div className="absolute top-3 right-3">
                      {getMealTypeBadge(recipe.meal_type)}
                    </div>
                  </div>
                  
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg text-gray-800 mb-2 line-clamp-2 group-hover:text-green-700 transition-colors">
                      {recipe.name}
                    </h3>
                    
                    {/* Nutrition Info */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <Flame className="h-4 w-4 text-orange-500" />
                        <span>{recipe.calories || 0} kcal</span>
                      </div>
                      {recipe.prep_time && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4 text-blue-500" />
                          <span>{recipe.prep_time} min</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Ingredients Preview */}
                    <p className="text-sm text-gray-500 line-clamp-2">
                      {recipe.ingredients?.slice(0, 4).join(', ')}
                      {recipe.ingredients?.length > 4 && '...'}
                    </p>
                    
                    {/* Macros */}
                    <div className="mt-3 pt-3 border-t flex justify-between text-xs text-gray-500">
                      <span>P: {recipe.protein || 0}g</span>
                      <span>C: {recipe.carbs || 0}g</span>
                      <span>G: {recipe.fat || 0}g</span>
                      <span>F: {recipe.fiber || 0}g</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pagination */}
      {totalPages > 1 && (
        <section className="py-8 border-t">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="pagination-prev"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
              
              <div className="flex items-center gap-2">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-lg transition-all ${
                        page === pageNum 
                          ? 'bg-green-600 text-white' 
                          : 'bg-white text-gray-600 hover:bg-green-50 border'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="pagination-next"
              >
                Următor
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">
              Pagina {page} din {totalPages}
            </p>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Vrei un plan alimentar personalizat?
          </h2>
          <p className="text-green-100 mb-8 max-w-xl mx-auto">
            Creează un cont gratuit și primește recomandări nutriționale bazate pe obiectivele tale de sănătate.
          </p>
          <Link to="/auth">
            <Button size="lg" className="bg-white text-green-700 hover:bg-green-50">
              Creează Cont Gratuit
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-gray-400">
        <div className="container mx-auto px-4 text-center">
          <p>© 2025 NutriHealth. Toate drepturile rezervate.</p>
        </div>
      </footer>
    </div>
  );
};

export default Recipes;
